/**
 * UrlExplorerPage (bd deepcontext-t06b) — Universe sidebar sub-page.
 *
 * bd deepcontext-d0i8: dropped the in-body title/subtitle, the hardcoded
 * decorative "Nigeria" market box, and the second (filtered-count) URL pill —
 * the header market selector + market-total URL pill that ClustersTopBar
 * portals for this route are the only market control and only URL pill now.
 * Search + the cluster filter join that same header row (portaled into
 * `#app-page-context`, after the MarketSelector). The page body is full-bleed
 * (no outer Card, see `pages/Clusters/index.tsx`'s `FULL_BLEED_PATHS`) and
 * the table paginates 15 rows per page via the shared `TableFillBox` +
 * `Pagination` components instead of a hand-rolled Previous/Next pair.
 *
 * Market selection mirrors ClustersPage (DEFAULT_MARKET = 'en' until d68.12
 * lands a `useClustersMarket()` context).
 */

import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
    isAbortError,
    extractApiError,
    clustersApi,
    ClustersUrlRow,
    TrendingStoryPayload,
} from '../../api/clusters';
import ClustersMarketContext from './contexts/ClustersMarketContext';
import {Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell} from '../../components/ui/table';
import {Select, SelectOption} from '../../components/ui/Select';
import {SearchInput} from '../../components/ui/SearchInput';
import {TableFillBox, tableFillHeightClass} from '../../components/ui/tableFillBox';
import Pagination from '../../components/ui/pagination';
import {usePortalSlot} from '../../hooks/usePortalSlot';
import type {PaginationInfo} from '../../types';

const PAGE_LIMIT = 15;
const SEARCH_DEBOUNCE_MS = 300;

// Series colors from the design. Stories carry no `palette_hue` of their own
// (that was a genre-cluster field) — the color dot now keys off a stable
// hash of the story id instead.
const SERIES_COLORS = ['#8D5DD9', '#0E9A52', '#EB5E9D', '#5A9CFF', '#C99700'];

function colorForStory(storyId: number): string {
    return SERIES_COLORS[storyId % SERIES_COLORS.length];
}

interface UrlExplorerPageProps {
    /**
     * Override the context-provided market. Production code leaves this
     * undefined and reads `selectedMarket` from `ClustersMarketContext`; tests
     * pass an explicit code to avoid having to mount the provider.
     */
    market?: string;
}

const UrlExplorerPage: React.FC<UrlExplorerPageProps> = ({
    market: marketProp,
}) => {
    const ctx = useContext(ClustersMarketContext);
    const market = marketProp ?? ctx?.selectedMarket ?? null;
    const [stories, setStories] = useState<TrendingStoryPayload[]>([]);
    const [storyId, setStoryId] = useState<string>('');

    const [rawQuery, setRawQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<string>('');

    const [rows, setRows] = useState<ClustersUrlRow[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Load stories for the dropdown once per market (top + ranked rest).
    // Cancel on unmount so a slow Universe payload doesn't paint a dropdown
    // on a stale tab (bd deepcontext-26s).
    useEffect(() => {
        if (!market) {
            setStories([]);
            return;
        }
        const controller = new AbortController();
        (async () => {
            try {
                const payload = await clustersApi.getClusters(market, {
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;
                setStories([...(payload.top ?? []), ...(payload.rest ?? [])]);
            } catch (err) {
                if (isAbortError(err) || controller.signal.aborted) return;
                // Non-fatal: dropdown will just show "All stories".
                setStories([]);
            }
        })();
        return () => {
            controller.abort();
        };
    }, [market]);

    // Reset the story filter and pagination whenever the market changes,
    // otherwise a stale storyId/offset from the prior market drives the
    // next fetch (deepcontext-6bm4).
    useEffect(() => {
        setStoryId('');
        setOffset(0);
    }, [market]);

    // Debounce the text input.
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedQuery(rawQuery.trim());
            setOffset(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [rawQuery]);

    // Re-fetch the URL list whenever inputs change. Cancels stale results so
    // the user typing fast / switching clusters fast doesn't race a slow
    // earlier request on top of the latest answer (bd deepcontext-26s).
    const fetchUrls = useCallback(
        async (signal: AbortSignal) => {
            if (!market) {
                setRows([]);
                setTotal(0);
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const data = await clustersApi.fetchClustersUrls(
                    {
                        market,
                        clusterId: storyId || null,
                        q: debouncedQuery || null,
                        limit: PAGE_LIMIT,
                        offset,
                    },
                    {signal},
                );
                if (signal.aborted) return;
                setRows(data.rows ?? []);
                setTotal(data.total ?? 0);
            } catch (err: any) {
                if (isAbortError(err) || signal.aborted) return;
                setError(extractApiError(err, 'Failed to load URLs'));
                setRows([]);
                setTotal(0);
            } finally {
                if (!signal.aborted) setLoading(false);
            }
        },
        [market, storyId, debouncedQuery, offset],
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchUrls(controller.signal);
        return () => {
            controller.abort();
        };
    }, [fetchUrls]);

    const storyMap = useMemo(() => {
        const map = new Map<number, TrendingStoryPayload>();
        for (const s of stories) map.set(s.id, s);
        return map;
    }, [stories]);

    // Options for the shared Select primitive (bd deepcontext-hkt0.4) — "All
    // stories" is a real option (value ''), not a separate placeholder, so
    // the trigger always shows a label even before the market resets it.
    const storyOptions = useMemo<SelectOption[]>(
        () => [
            {value: '', label: 'All stories'},
            ...stories.map((s) => ({
                value: String(s.id),
                label: `${s.headline ?? s.event ?? `Story ${s.id}`} (${s.member_count})`,
            })),
        ],
        [stories],
    );

    // PaginationInfo for the shared Pagination component (deepcontext-d0i8) —
    // mirrors SiteLists.tsx's domainsPaginationInfo, built off the existing
    // offset/total state rather than a page-indexed fetch.
    const page = Math.floor(offset / PAGE_LIMIT) + 1;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
    const paginationInfo: PaginationInfo = {
        page,
        limit: PAGE_LIMIT,
        totalCount: total,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
        nextPage: page < totalPages ? page + 1 : null,
        previousPage: page > 1 ? page - 1 : null,
    };
    const isFullPage = total >= PAGE_LIMIT;

    // Portal the search input + story select into the shared page-header
    // context slot (deepcontext-d0i8), joining the MarketSelector that
    // ClustersTopBar already portals there — the mockup puts Market, search,
    // and the cluster filter on one row. The slot's own flex row already
    // places MarketSelector first in DOM order (ClustersTopBar renders
    // before ClustersRoutes in pages/Clusters/index.tsx), so this wrapper
    // just needs to grow into the remaining width.
    const contextSlot = usePortalSlot('app-page-context');

    return (
        <div className="flex flex-col h-full min-h-0 gap-4" data-testid="url-explorer-page">
            {/* Search input + cluster select, portaled onto the header row
                next to the market selector (bd deepcontext-d0i8) — both at
                the app's 42px row-control standard (bd deepcontext-hkt0.4).
                The search wrapper grows (flex-1 min-w-0); the select keeps a
                fixed width. */}
            {contextSlot &&
                createPortal(
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                        <SearchInput
                            value={rawQuery}
                            onChange={(e) => setRawQuery(e.target.value)}
                            placeholder="Search title, domain, or path…"
                            className="flex-1 min-w-0"
                            maxLength={200}
                            data-testid="url-explorer-search-input"
                        />
                        <div className="w-[250px] flex-shrink-0">
                            <Select
                                value={storyId}
                                onChange={(value) => {
                                    setStoryId(value);
                                    setOffset(0);
                                }}
                                options={storyOptions}
                                data-testid="url-explorer-cluster-select"
                            />
                        </div>
                    </div>,
                    contextSlot,
                )}

            {error && (
                <div
                    className="rounded-md border border-risk bg-risk-bg px-3 py-2 text-sm text-risk"
                    role="alert"
                    data-testid="url-explorer-error"
                >
                    {error}
                </div>
            )}

            {/* Table card with 16px (rounded-2xl) radius; pagination renders
                as the shared TableFillBox footer (bd deepcontext-d0i8) —
                mirrors BlueprintsTable.tsx's TableFillBox + Pagination
                pattern, 15 rows per page. */}
            <TableFillBox
                isFullPage={isFullPage}
                footer={
                    paginationInfo.totalPages > 1 ? (
                        <Pagination
                            pagination={paginationInfo}
                            onPageChange={(nextPage) => setOffset((nextPage - 1) * PAGE_LIMIT)}
                        />
                    ) : undefined
                }
                className="rounded-2xl border border-line bg-surface shadow-sm"
                data-testid="url-explorer-table-card"
            >
                {loading ? (
                    <div
                        className="px-4 py-8 text-center text-xs text-ink-soft"
                        data-testid="url-explorer-loading"
                    >
                        Loading URLs…
                    </div>
                ) : rows.length === 0 ? (
                    <div
                        className="px-4 py-8 text-center text-xs text-ink-soft"
                        data-testid="url-explorer-empty"
                    >
                        No URLs match.
                    </div>
                ) : (
                    <Table className={tableFillHeightClass(isFullPage)} data-testid="url-explorer-table">
                        <TableHead>
                            <tr>
                                <TableHeaderCell>Title</TableHeaderCell>
                                <TableHeaderCell>Domain</TableHeaderCell>
                                <TableHeaderCell>Story</TableHeaderCell>
                                <TableHeaderCell>URL</TableHeaderCell>
                            </tr>
                        </TableHead>
                        <TableBody>
                            {rows.map((u) => {
                                const story = storyMap.get(u.story_id);
                                return (
                                    <TableRow
                                        key={`${u.url_hash}-${u.story_id}`}
                                        className="hover:bg-ghost"
                                        data-testid="url-explorer-row"
                                    >
                                        <TableCell className="font-bold">
                                            {u.title || u.site_domain || '—'}
                                        </TableCell>
                                        <TableCell className="text-ink-soft">
                                            {u.site_domain}
                                        </TableCell>
                                        <TableCell>
                                            {story ? (
                                                <span
                                                    className="inline-flex items-center gap-1.5 rounded-full bg-ghost px-3 text-ink"
                                                    style={{
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        padding: '3px 11px',
                                                    }}
                                                    data-testid="url-explorer-story-chip"
                                                >
                                                    <span
                                                        className="inline-block rounded-full"
                                                        style={{
                                                            width: '7px',
                                                            height: '7px',
                                                            backgroundColor: colorForStory(u.story_id),
                                                        }}
                                                        data-testid="story-color-dot"
                                                    />
                                                    {story.headline ?? story.event ?? `Story ${story.id}`}
                                                </span>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className="max-w-xs"
                                            style={{ maxWidth: '220px' }}
                                            data-testid="url-explorer-url-cell"
                                        >
                                            <a
                                                href={u.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-block truncate font-medium text-accent-ink hover:underline"
                                                style={{
                                                    maxWidth: '220px',
                                                    fontWeight: 600,
                                                }}
                                                title={u.url}
                                            >
                                                {u.url}
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableFillBox>
        </div>
    );
};

export default UrlExplorerPage;
